import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  Person as PersonIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { makeApiCall } from '../../config/api';
import { getWageTypeOptions } from '../../utils/wageUtils';

const EmployeeForm = ({ employee, onSuccess, onCancel, lookups }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state - focused purely on employee data
  const [formData, setFormData] = useState({
    employee_name: '',
    wage: '',
    wage_type: 'hourly',
    department_id: '',
    job_title_id: '',
    position_id: '',
    hire_date: new Date().toISOString().split('T')[0],
    is_active: true,
    // Optional user linking (can be set later)
    user_id: null
  });

  // Available users for linking
  const [availableUsers, setAvailableUsers] = useState([]);

  // Initialize form with employee data if editing
  useEffect(() => {
    if (employee) {
      console.log('Initializing form with employee:', employee);
      setFormData({
        employee_name: employee.employee_name || '',
        wage: employee.wage !== null && employee.wage !== undefined ? employee.wage : '',
        wage_type: employee.wage_type || 'hourly',
        department_id: employee.department_id !== null && employee.department_id !== undefined ? employee.department_id : '',
        job_title_id: employee.job_title_id !== null && employee.job_title_id !== undefined ? employee.job_title_id : '',
        position_id: employee.position_id !== null && employee.position_id !== undefined ? employee.position_id : '',
        hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : new Date().toISOString().split('T')[0],
        is_active: employee.is_active !== null && employee.is_active !== undefined ? employee.is_active : true,
        user_id: employee.user_id || null
      });
    }
  }, [employee]);

  // Fetch available users for linking
  useEffect(() => {
    fetchAvailableUsers();
  }, []);

  const fetchAvailableUsers = async () => {
    try {
      const response = await makeApiCall('api/employees/available-users');

      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching available users:', error);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log(`handleChange: name=${name}, type=${type}, value=${value}, checked=${checked}`);
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle user selection for linking
  const handleUserChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      user_id: newValue ? newValue.id : null
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.employee_name.trim()) {
      setError('Employee name is required');
      setLoading(false);
      return;
    }

    try {
      const endpoint = employee 
        ? `api/employees/${employee.id}`
        : 'api/employees';
      
      const method = employee ? 'PUT' : 'POST';

      // Prepare data for submission
      const submitData = {
        ...formData,
        wage: formData.wage ? parseFloat(formData.wage) : null,
        department_id: formData.department_id || null,
        job_title_id: formData.job_title_id || null,
        position_id: formData.position_id || null,
        hire_date: formData.hire_date || null
      };

      console.log('Submitting employee data:', submitData);

      const response = await makeApiCall(endpoint, {
        method,
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save employee');
      }

      const data = await response.json();
      onSuccess(data.message || 'Employee saved successfully');

    } catch (error) {
      console.error('Error saving employee:', error);
      setError(error.message || 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  // Find selected user for autocomplete
  const selectedUser = availableUsers.find(user => user.id === formData.user_id) || null;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Employee Information */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            pb: 1,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <PersonIcon />
          Employee Information
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              name="employee_name"
              label="Employee Name"
              value={formData.employee_name}
              onChange={handleChange}
              placeholder="Full name as it appears on records"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              name="wage"
              label="Wage Amount"
              type="number"
              step="0.01"
              value={formData.wage}
              onChange={handleChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                name="wage_type"
                value={formData.wage_type}
                onChange={handleChange}
                label="Type"
              >
                {getWageTypeOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              name="hire_date"
              label="Hire Date"
              type="date"
              value={formData.hire_date}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleChange}
                  name="is_active"
                />
              }
              label="Active Employee"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Job Information */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'secondary.main',
            borderBottom: '2px solid',
            borderColor: 'secondary.main',
            pb: 1,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <WorkIcon />
          Job Details
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel>Department</InputLabel>
              <Select
                name="department_id"
                value={formData.department_id}
                label="Department"
                onChange={handleChange}
              >
                <MenuItem value="">No Department</MenuItem>
                {lookups.departments?.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel>Job Title</InputLabel>
              <Select
                name="job_title_id"
                value={formData.job_title_id}
                label="Job Title"
                onChange={handleChange}
              >
                <MenuItem value="">No Job Title</MenuItem>
                {lookups.job_titles?.map((title) => (
                  <MenuItem key={title.id} value={title.id}>
                    {title.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel>Position</InputLabel>
              <Select
                name="position_id"
                value={formData.position_id}
                label="Position"
                onChange={handleChange}
              >
                <MenuItem value="">No Position</MenuItem>
                {lookups.employee_positions?.map((pos) => (
                  <MenuItem key={pos.id} value={pos.id}>
                    {pos.position_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* System Access Linking */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'info.main',
            borderBottom: '2px solid',
            borderColor: 'info.main',
            pb: 1,
            mb: 3
          }}
        >
          System Access (Optional)
        </Typography>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Autocomplete
              value={selectedUser}
              onChange={handleUserChange}
              options={availableUsers}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name} (@${option.username})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Link to User Account"
                  placeholder="Search for existing user to link..."
                  helperText="Optional: Link this employee to a system user account"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {option.first_name} {option.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      @{option.username} • {option.email}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Form Actions */}
      <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Saving...' : (employee ? 'Update Employee' : 'Create Employee')}
        </Button>
      </Box>
    </Box>
  );
};

export default EmployeeForm; 